// Compose-from-scratch email dialog (Shared Inbox cutover Phase 2, item 2). Phase A only —
// the actual send call is wired in Phase B via the onSend prop; this component owns the form,
// client-side validation, and attachment-cap enforcement.

import { useEffect, useState } from "react";
import { Loader2, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { EmailAttachmentPicker, useEmailAttachments } from "./EmailAttachmentPicker";

/** Mirrors POST /api/emails/compose's body (server/lib/emailCompose.ts + Phase B route). */
export interface ComposeEmailPayload {
  mailbox: string;
  account_id?: string | null;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
  attachments?: { name: string; contentType: string; contentBytes: string }[];
}

export interface SendableMailbox {
  mailbox_address: string;
  display_name?: string | null;
  is_shared_mailbox?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Active mailboxes the current staffer may send from (shared + eventually "My Mail"). */
  fromOptions: SendableMailbox[];
  /** Pre-select a mailbox — e.g. the pop-out's currently-selected mailbox, or an account's mapped one. */
  defaultFrom?: string;
  defaultAccountId?: string | null;
  /** Optional To autocomplete source (account contacts). Purely a suggestion list — no validation tie-in. */
  contactSuggestions?: { name: string; email: string }[];
  /**
   * Phase A stub: Phase B wires this to emailApi.composeEmail(payload). Throw to signal failure —
   * the dialog shows the error and stays open so the draft isn't lost.
   */
  onSend: (payload: ComposeEmailPayload) => Promise<void>;
}

function parseRecipients(input: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input.split(/[,;]/)) {
    const addr = raw.trim();
    if (!addr) continue;
    const key = addr.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(addr);
  }
  return out;
}

export function ComposeEmailDialog({
  open,
  onClose,
  fromOptions,
  defaultFrom,
  defaultAccountId,
  contactSuggestions,
  onSend,
}: Props) {
  const { toast } = useToast();

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const attach = useEmailAttachments();
  const [sending, setSending] = useState(false);

  // Reset the form fresh every time the dialog opens. Signature is added automatically
  // server-side (server/lib/emailSignature.ts) — the compose box stays plain.
  useEffect(() => {
    if (!open) return;
    setFrom(defaultFrom ?? fromOptions[0]?.mailbox_address ?? "");
    setTo("");
    setCc("");
    setBcc("");
    setSubject("");
    setBody("");
    attach.clear();
  }, [open, defaultFrom]); // eslint-disable-line react-hooks/exhaustive-deps

  const overCap = attach.overCap;

  const handleSend = async () => {
    const recipients = parseRecipients(to);
    if (!from) {
      toast({ title: "Choose a sending mailbox", variant: "destructive" });
      return;
    }
    if (!recipients.length) {
      toast({ title: "At least one recipient is required", variant: "destructive" });
      return;
    }
    if (!subject.trim()) {
      toast({ title: "Subject is required", variant: "destructive" });
      return;
    }
    if (!body.trim()) {
      toast({ title: "Message body is required", variant: "destructive" });
      return;
    }
    if (overCap) {
      toast({ title: "Attachments exceed the 2.5 MB total limit", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      await onSend({
        mailbox: from,
        account_id: defaultAccountId ?? null,
        to: recipients,
        cc: cc.trim() ? parseRecipients(cc) : undefined,
        bcc: bcc.trim() ? parseRecipients(bcc) : undefined,
        subject: subject.trim(),
        body,
        attachments: attach.payload(),
      });
      toast({
        title: "Email sent",
        description: `From ${from} — it will appear in the archive within a few minutes.`,
      });
      onClose();
    } catch (err: any) {
      toast({ title: "Could not send email", description: err?.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>New email</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a sending mailbox" />
              </SelectTrigger>
              <SelectContent>
                {fromOptions.map((opt) => (
                  <SelectItem key={opt.mailbox_address} value={opt.mailbox_address}>
                    {opt.display_name ? `${opt.display_name} (${opt.mailbox_address})` : opt.mailbox_address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              list={contactSuggestions?.length ? "compose-contact-suggestions" : undefined}
            />
            {contactSuggestions?.length ? (
              <datalist id="compose-contact-suggestions">
                {contactSuggestions.map((c) => (
                  <option key={c.email} value={c.email}>
                    {c.name}
                  </option>
                ))}
              </datalist>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Cc <span className="text-muted-foreground">(optional)</span></Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="comma-separated" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Bcc <span className="text-muted-foreground">(optional)</span></Label>
              <Input value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="comma-separated" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Message</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="min-h-[180px] text-sm"
            />
            <p className="text-[11px] text-muted-foreground">Your signature is added automatically.</p>
          </div>

          <EmailAttachmentPicker state={attach} disabled={sending} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending || overCap}>
            {sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
